import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Funnel from "@/pages/Funnel";
import Leads from "@/pages/Leads";
import Quotations from "@/pages/Quotations";
import Tasks from "@/pages/Tasks";
import Reports from "@/pages/Reports";
import FunnelBuilder from "@/pages/FunnelBuilder";
import Settings from "@/pages/Settings";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#0B192C]" data-testid="app-loading">
      <div className="text-white font-display text-xl tracking-[0.2em]">SERATO <span className="text-orange-500">SALESCORE</span></div>
    </div>
  );
}

function Guard({ perms, children }) {
  const { can } = useAuth();
  if (perms && !can(...perms)) {
    return (
      <div className="p-10" data-testid="access-denied">
        <h2 className="text-2xl font-bold">Access restricted</h2>
        <p className="text-slate-500 mt-2">Your role does not include permission to view this page.</p>
      </div>
    );
  }
  return children;
}

function Protected() {
  const { user, meta } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!meta) return <Loading />;
  const home = user.permissions.includes("*") || user.permissions.includes("dashboard.view") ? "/dashboard" : "/leads";
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to={home} replace />} />
        <Route path="/dashboard" element={<Guard perms={["dashboard.view"]}><Dashboard /></Guard>} />
        <Route path="/funnels/:market" element={<Guard perms={["leads.view_all", "leads.view_own"]}><Funnel /></Guard>} />
        <Route path="/leads" element={<Guard perms={["leads.view_all", "leads.view_own"]}><Leads /></Guard>} />
        <Route path="/quotations" element={<Guard perms={["quotations.view"]}><Quotations /></Guard>} />
        <Route path="/tasks" element={<Guard perms={["leads.view_all", "leads.view_own"]}><Tasks /></Guard>} />
        <Route path="/reports" element={<Guard perms={["reports.view"]}><Reports /></Guard>} />
        <Route path="/funnel-builder" element={<Guard perms={["workflows.edit"]}><FunnelBuilder /></Guard>} />
        <Route path="/settings" element={<Guard perms={["users.manage", "settings.manage", "sources.manage", "audit.view"]}><Settings /></Guard>} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </AppShell>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<Protected />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
